import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { MapPin } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export const LOCAL_FAQS = [
  {
    q: "Where is Zebaa located?",
    a: "Zebaa is at Unit 1, 312 Green Street, London, E13 9AP - in the heart of East London's famous Green Street, moments from Upton Park and easily reached from East Ham, Forest Gate and across Newham.",
  },
  {
    q: "What does Zebaa sell?",
    a: "We are a South Asian imitation jewellery shop. Our collection covers bridal sets, kundan-style necklaces, polki-style chokers, temple jewellery, jhumkas, chandbalis, gold-tone bangles, kadas, chudi stacks, meenakari sets and maang tikkas - perfect for weddings, Eid, mehndi and every special occasion.",
  },
  {
    q: "Is your jewellery real gold or fine jewellery?",
    a: "No - everything at Zebaa is imitation (artificial) jewellery. That means gold-tone alloys, kundan-style settings, faux pearls and coloured stones - designed to look the part on the big day without the price tag of real gold or diamonds.",
  },
  {
    q: "Do you offer bridal jewellery in East London?",
    a: "Yes - Zebaa is one of Green Street's newest South Asian bridal jewellery destinations, offering hand-embellished bridal sets and matching accessories with in-store styling.",
  },
  {
    q: "What are your opening hours?",
    a: "We are open Monday to Saturday 10am–7pm and Sunday 11am–5pm. Visit us in-store on Green Street or shop the collection online with UK delivery.",
  },
  {
    q: "Do you deliver across the UK?",
    a: "Yes - we ship UK-wide with Royal Mail Tracked 48 (2–3 working days), with free delivery on orders over £40.",
  },
  {
    q: "Where can I buy South Asian bridal jewellery in East London?",
    a: "Zebaa at Unit 1, 312 Green Street, London E13 9AP is one of Green Street's newest South Asian imitation jewellery shops for bridal sets, kundan-style necklaces and gold-tone bangles - moments from Upton Park and easily reached from East Ham, Forest Gate and across Newham. You can also shop the full collection online with UK-wide delivery.",
  },
  {
    q: "What is your returns policy?",
    a: "Standard in-stock items can be returned within 14 days in line with our returns policy. Earrings can only be returned unworn and in their original packaging for hygiene reasons.",
  },
  {
    q: "Do you have a loyalty or rewards scheme?",
    a: "Yes - you earn £1 in rewards for every £25 you spend at Zebaa, which you can put towards future orders.",
  },
  {
    q: "How can I contact Zebaa?",
    a: "You can email us at hello@zebaa.uk, message us on Instagram @zebaa.uk, or visit us in-store at Unit 1, 312 Green Street, London E13 9AP.",
  },
];

export const LocalSeoSection = () => (
  <section className="bg-surface py-16 lg:py-24" data-testid="local-seo-section" aria-labelledby="local-seo-heading">
    <div className="max-w-[1800px] mx-auto px-5 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
      <Reveal>
        <h2 id="local-seo-heading" className="font-serif text-3xl lg:text-[2.6rem] text-charcoal leading-tight">
          East London's Home of South Asian Imitation Jewellery
        </h2>
        <div
          className="w-14 h-px mt-6"
          style={{ background: "linear-gradient(90deg, #C9A24B 0%, #E4C87F 60%, transparent 100%)" }}
        />
        <div className="text-gray-600 mt-7 space-y-4 leading-relaxed text-[15px]">
          <p>
            <span className="text-charcoal font-medium">Zebaa</span> is Green Street's newest
            South Asian imitation jewellery shop - bridal sets, kundan-style necklaces, gold-tone
            bangles and chandbalis, carefully selected from partner workshops in Jaipur and
            Hyderabad. Everything we stock is imitation (artificial) jewellery, designed to look
            the part on the big day without the price tag of fine jewellery.
          </p>
          <p>
            Whether you're searching for a bridal jewellery set in London, kundan-style necklaces
            in Upton Park, or gold-tone bangles near East Ham and Forest Gate, our Green Street
            shop is your destination. Visit us in-store, or shop the full collection online with
            fast Royal Mail tracked UK delivery.
          </p>
        </div>

        <div className="mt-9 flex items-center gap-4 bg-white rounded-xl px-6 py-5 border border-black/[0.06] shadow-[0_24px_50px_-34px_rgba(26,26,26,0.35)]">
          <span className="w-11 h-11 rounded-full bg-pink/10 flex items-center justify-center shrink-0">
            <MapPin className="w-5 h-5 text-pink" />
          </span>
          <div className="text-sm">
            <p className="font-semibold text-charcoal">Zebaa - Green Street</p>
            <p className="text-gray-600 mt-0.5">Unit 1, 312 Green Street, London, E13 9AP</p>
            <p className="text-gray-500 mt-0.5">Mon–Sat 10am–7pm · Sun 11am–5pm</p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <Link to="/shop/all" className="bg-pink text-white px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.16em] hover:bg-pink-hover transition-colors">Shop the Collection</Link>
          <Link to="/contact" className="text-xs uppercase tracking-[0.16em] text-charcoal border-b border-gold/60 pb-1 hover:border-pink hover:text-pink transition-colors">Visit the Store</Link>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="bg-white rounded-xl border border-black/[0.06] p-6 sm:p-8 shadow-[0_24px_50px_-34px_rgba(26,26,26,0.35)]">
          <h3 className="font-serif text-2xl text-charcoal">Frequently Asked Questions</h3>
          <div
            className="w-14 h-px mt-3 mb-2"
            style={{ background: "linear-gradient(90deg, #C9A24B 0%, #E4C87F 60%, transparent 100%)" }}
          />
          <Accordion type="single" collapsible className="w-full" data-testid="local-faq">
            {LOCAL_FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-black/[0.07]">
                <AccordionTrigger className="text-left text-charcoal hover:text-pink font-sans font-medium text-[15px]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Reveal>
    </div>
  </section>
);
